import { lazy, Suspense } from "react";
import { useSelector } from "react-redux";
import { Navigate, Outlet, RouteObject, useLocation } from "react-router-dom";
import { selectCurrentUser, selectIsLoggedIn } from "store/user/user.selector";
import { authLinks } from "utils/helper/links";
import { REDIRECT_URI } from "utils/helper/states";
import NotPermitted from "./forbidden/page";
import ErrorPage from "components/error/error.component";
import { ticketLoader } from "loaders/ticket";
import Preloader from "components/preloader";
import NotificationPage from "./dashboard/notification";
import EditProduct from "./dashboard/product/products/edit";
import { productLoader, productsLoader } from "loaders/products";
import { orderDetailsLoader } from "loaders/order";

const VerifyEmail = lazy(() => import("./auth/verify"));
const CompleteRegistration = lazy(() => import("./auth/complete-registration"));

const DashboardHome = lazy(() => import("./dashboard"));
const ProfilePage = lazy(() => import("./dashboard/profile"));
const ProductsPage = lazy(() => import("./dashboard/product/products"));
const ProductsReviews = lazy(
  () => import("./dashboard/product/products-reviews")
);
const ProductsReport = lazy(() => import("./dashboard/reports/products"));
const ProductsWishlist = lazy(
  () => import("./dashboard/reports/product-wishlist")
);
const SupportPage = lazy(() => import("./dashboard/support"));
const AddProduct = lazy(
  () => import("./dashboard/product/products/add-product")
);
const KycPage = lazy(() => import("./dashboard/kyc"));
const AddTicket = lazy(() => import("./dashboard/support/add"));
const ViewResponses = lazy(() => import("./dashboard/support/replies"));

const Orders = lazy(() => import("./dashboard/orders"));
const OrderDetails = lazy(() => import("./dashboard/orders/view"));
const AnalyticsPage = lazy(() => import("./dashboard/analytics"));
const WalletPage = lazy(() => import("./dashboard/wallet"));

// Routes that don't require email verification
const UNVERIFIED_ALLOWED_ROUTES = ["/verify-email"];

function ProtectedRoutes() {
  const isLoggedIn = useSelector(selectIsLoggedIn),
    user = useSelector(selectCurrentUser);
  const location = useLocation();

  // Store the full URI including search (query params) and hash (fragments)
  const fullUri = `${location.pathname}${location.search}${location.hash}`;

  // Check if user is logged in and is a vendor
  if (isLoggedIn && user != null) {
    // Check if the current route is allowed for unverified users
    const isUnverifiedAllowedRoute = UNVERIFIED_ALLOWED_ROUTES.some((route) =>
      location.pathname.startsWith(route)
    );

    // If email is not verified and route requires verification, redirect to verify email page

    if (!user.email_verified && !isUnverifiedAllowedRoute) {
      return <Navigate to="/verify-email" replace />;
    }

    // If email is not verified and route requires verification, redirect to verify email page
    if (
      !user.shop &&
      !location.pathname.startsWith("/auth/complete-registration") &&
      !isUnverifiedAllowedRoute
    ) {
      return <Navigate to="/auth/complete-registration" replace />;
    }

    return user.type !== "vendor" ? <NotPermitted /> : <Outlet />;
  }

  sessionStorage.setItem(REDIRECT_URI, fullUri);
  // Redirect to the login page
  return <Navigate to={authLinks.login} replace />;
}

export const protectedRoutes: RouteObject[] = [
  {
    path: "/verify-email",
    element: <VerifyEmail />,
  },
  {
    path: "/auth/complete-registration",
    element: <CompleteRegistration />,
  },
  // dashboard
  {
    path: "/dashboard",
    element: <DashboardHome />,
  },
  {
    path: "/dashboard/profile",
    element: <ProfilePage />,
  },
  {
    path: "/dashboard/product/products",
    element: <ProductsPage />,
    loader: productsLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: "/dashboard/product/products/add-product",
    element: <AddProduct />,
  },

  {
    path: "/dashboard/product/products/:slug",
    element: <EditProduct />,
    loader: productLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: "/dashboard/product/products-reviews",
    element: <ProductsReviews />,
  },
  {
    path: "/dashboard/reports/products",
    element: <ProductsReport />,
  },
  {
    path: "/dashboard/reports/product-wishlist",
    element: <ProductsWishlist />,
  },
  {
    path: "/dashboard/notifications",
    element: <NotificationPage />,
  },
  {
    path: "/dashboard/support/tickets",
    element: <SupportPage />,
  },
  {
    path: "/dashboard/support/add-ticket",
    element: <AddTicket />,
  },
  //
  {
    path: "/dashboard/orders",
    element: <Orders />,
  },
  {
    path: "/dashboard/analytics",
    element: <AnalyticsPage />,
  },
  {
    path: "/dashboard/wallet",
    element: <WalletPage />,
  },
  {
    path: "/dashboard/orders/:code",
    element: <OrderDetails />,
    loader: orderDetailsLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: "/dashboard/support/tickets/:code",
    element: <ViewResponses />,
    loader: ticketLoader,
    errorElement: <ErrorPage />,
  },
  {
    path: "/dashboard/kyc",
    element: <KycPage />,
  },
];

export default ProtectedRoutes;
